import {Parameter} from 'platform/components';
import {useDateTimeFormatter} from 'platform/locale';

import i18n from '@dms/i18n';

import {Nullish, buildArray, parseDate, isNotNilOrEmpty, EMPTY_PLACEHOLDER} from 'shared/web';

interface HeaderData {
  publicId: string | Nullish;
  externalId: string | Nullish;
  vin: string | Nullish;
  registrationPlate: string | Nullish;
  firstRegistrationOn: string | Nullish;
  warrantyDate: string | Nullish;
  isServiceVehicle: boolean | Nullish;
  technicalInspectionValidUntil: string | Nullish;
  engineCode: string | Nullish;
  hasComments?: boolean;
  additionalNote?: string | Nullish;
  onIconClick?: () => void;
}

export function useGetVehicleHeaderV3(data: HeaderData): Parameter[] {
  const formatDateTime = useDateTimeFormatter();
  const formatDate = (dateValue: string) => formatDateTime('dateShort', parseDate(dateValue));

  return buildArray<Parameter>()
    .when(isNotNilOrEmpty(data.additionalNote), {
      icon: 'action/description',
      title: null,
      variant: 'secondary',
      tooltip: data.additionalNote,
      onClick: data.onIconClick,
    })
    .when(data.hasComments, {
      icon: 'communication/comment',
      title: null,
      variant: 'secondary',
      hideSeparator: true,
      onClick: data.onIconClick,
    })
    .add({
      title: data?.publicId || i18n.t('entity.vehicle.notifications.noPublicId'),
      tooltip: i18n.t('entity.vehicle.labels.publicId'),
      variant: 'secondary',
    })
    .when(isNotNilOrEmpty(data?.externalId), {
      title: data?.externalId || i18n.t('entity.vehicle.notifications.noExternalId'),
      tooltip: i18n.t('entity.vehicle.labels.externalId'),
      variant: 'secondary',
    })
    .add({
      title: data?.vin || i18n.t('entity.vehicle.notifications.noVIN'),
      tooltip: i18n.t('entity.vehicle.labels.vin'),
      variant: 'secondary',
    })
    .add({
      title: data?.registrationPlate || i18n.t('entity.vehicle.notifications.noLicencePlate'),
      tooltip: i18n.t('entity.vehicle.labels.licensePlate'),
      variant: 'secondary',
    })
    .when(data.isServiceVehicle, {
      title: data?.technicalInspectionValidUntil
        ? formatDate(data?.technicalInspectionValidUntil)
        : EMPTY_PLACEHOLDER,
      tooltip: i18n.t('entity.workshop.labels.technicalInspectionValidUntil'),
    })
    .when(data.isServiceVehicle, {
      title: data?.engineCode || EMPTY_PLACEHOLDER,
      tooltip: i18n.t('entity.vehicle.labels.engineCode'),
    })
    .when(data.isServiceVehicle, {
      title: data?.firstRegistrationOn
        ? formatDate(data?.firstRegistrationOn)
        : i18n.t('entity.vehicle.notifications.noFirstRegistration'),
      tooltip: i18n.t('entity.vehicle.labels.firstRegistration'),
      variant: 'secondary',
    })
    .when(data.isServiceVehicle, {
      title: data?.warrantyDate
        ? formatDate(data?.warrantyDate)
        : i18n.t('entity.vehicle.notifications.noWarranty'),
      tooltip: i18n.t('entity.vehicle.labels.warrantyUntil'),
      variant: 'secondary',
    });
}
